import { useTenant } from '../contexts/TenantContext';
import { useDashboardData } from '../contexts/DashboardDataContext';
import { 
  Wallet,
  Send,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Info
} from 'lucide-react';

/**
 * CWDashboard - CloudWaitress-styled dashboard page
 * 
 * ONLY for /__ui-test/cloudwaitress/dashboard
 * Uses existing data from DashboardDataContext
 * No new API calls, no business logic changes
 */
export default function CWDashboard() {
  const { tenant } = useTenant();
  const dashboardData = useDashboardData();
  
  const summary = dashboardData?.dashboardSummary;
  const walletStatus = dashboardData?.walletStatus;
  const recentActivity = dashboardData?.recentActivity || [];
  const settings = dashboardData?.settings;

  // Show loading state briefly if data not ready
  if (!summary && !walletStatus) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="h-32 bg-gray-200 rounded-lg"></div>
            <div className="h-32 bg-gray-200 rounded-lg"></div>
            <div className="h-32 bg-gray-200 rounded-lg"></div>
            <div className="h-32 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-1">Dashboard</h1>
        <p className="text-sm text-gray-600">
          Welcome back, {tenant?.user?.first_name || 'User'}
        </p>
      </div>

      {/* Widgets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        
        {/* 1. Wallet Summary Card */}
        <WalletSummaryCard walletStatus={walletStatus} />
        
        {/* 2. Campaign Performance Snapshot */}
        <CampaignSnapshotCard summary={summary} />
        
        {/* 3. Usage This Period */}
        <UsageThisPeriodCard summary={summary} />
        
        {/* 4. System Status */}
        <SystemStatusCard tenant={tenant} settings={settings} />
      </div>

      {/* Recent Activity (if available) */}
      {recentActivity && recentActivity.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Recent Activity</h3>
          <div className="space-y-2">
            {recentActivity.slice(0, 5).map((activity, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs py-2 border-b border-gray-100 last:border-0">
                <span className="text-gray-600">{activity.description || 'Transaction'}</span>
                <span className="text-gray-900 font-medium">
                  {activity.amount_cents ? `$${(activity.amount_cents / 100).toFixed(2)}` : '—'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// 1. Wallet Summary Card Component
function WalletSummaryCard({ walletStatus }) {
  const smsRemaining = walletStatus?.sms_capacity || 0;
  const walletBalanceCents = walletStatus?.wallet_balance_cents || 0;
  const walletBalance = (walletBalanceCents / 100).toFixed(2);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
          <Wallet className="w-5 h-5 text-orange-600" />
        </div>
        <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">Wallet</span>
      </div>
      
      <div className="space-y-2">
        <div>
          <div className="text-3xl font-semibold text-gray-900">
            {smsRemaining.toLocaleString()}
          </div>
          <div className="text-xs text-gray-600">SMS Credits Remaining</div>
        </div>
        
        <div className="pt-2 border-t border-gray-100">
          <div className="text-sm text-gray-600">
            Wallet Balance: <span className="font-semibold text-gray-900">${walletBalance} AUD</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// 2. Campaign Performance Snapshot
function CampaignSnapshotCard({ summary }) {
  const totalCampaigns = summary?.total_campaigns || 0;
  const lastCampaignSent = summary?.campaigns_sent_this_month || 0;
  
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
          <Send className="w-5 h-5 text-blue-600" />
        </div>
        <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">Campaigns</span>
      </div>
      
      <div className="space-y-2">
        <div>
          <div className="text-3xl font-semibold text-gray-900">
            {totalCampaigns}
          </div>
          <div className="text-xs text-gray-600">Total Campaigns</div>
        </div>
        
        {lastCampaignSent > 0 ? (
          <div className="pt-2 border-t border-gray-100">
            <div className="text-sm text-gray-600">
              This month: <span className="font-semibold text-gray-900">{lastCampaignSent}</span>
            </div>
          </div>
        ) : (
          <div className="pt-2 border-t border-gray-100">
            <div className="text-xs text-gray-500 italic">No campaigns sent this month</div>
          </div>
        )}
      </div>
    </div>
  );
}

// 3. Usage This Period
function UsageThisPeriodCard({ summary }) {
  const smsSentThisMonth = summary?.sms_sent_this_month || 0;
  const smsSentLastMonth = summary?.sms_sent_last_month || 0;
  
  const hasIncrease = smsSentThisMonth > smsSentLastMonth;
  const percentChange = smsSentLastMonth > 0 
    ? (((smsSentThisMonth - smsSentLastMonth) / smsSentLastMonth) * 100).toFixed(0)
    : 0;
  
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-green-600" />
        </div>
        <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">Usage</span>
      </div>
      
      <div className="space-y-2">
        <div>
          <div className="text-3xl font-semibold text-gray-900">
            {smsSentThisMonth.toLocaleString()}
          </div>
          <div className="text-xs text-gray-600">SMS Sent This Month</div>
        </div>
        
        {smsSentLastMonth > 0 && percentChange !== 0 && (
          <div className="pt-2 border-t border-gray-100">
            <div className="text-xs">
              <span className={`font-semibold ${hasIncrease ? 'text-green-600' : 'text-red-600'}`}>
                {hasIncrease ? '+' : ''}{percentChange}%
              </span>
              <span className="text-gray-600"> vs last month</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// 4. System Status Card
function SystemStatusCard({ tenant, settings }) {
  const senderIdStatus = settings?.sender_id || 'Not configured';
  const accountActive = tenant?.has_business;
  
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
          <Info className="w-5 h-5 text-purple-600" />
        </div>
        <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">Status</span>
      </div>
      
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          {accountActive ? (
            <>
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm text-gray-900">Account Active</span>
            </>
          ) : (
            <>
              <AlertCircle className="w-4 h-4 text-orange-600" />
              <span className="text-sm text-gray-900">Setup Required</span>
            </>
          )}
        </div>
        
        <div className="pt-2 border-t border-gray-100 space-y-1">
          <div className="text-xs text-gray-600">
            Sender ID: <span className="font-medium text-gray-900">{senderIdStatus}</span>
          </div>
          <div className="text-xs text-gray-600">
            Business: <span className="font-medium text-gray-900">{tenant?.business?.name || 'N/A'}</span>
          </div>
        </div>
        
        <div className="pt-2 border-t border-gray-100">
          <div className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-700 px-2 py-1 rounded">
            <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
            Test Environment
          </div>
        </div>
      </div>
    </div>
  );
}
